import { MessageBar, MessageBarType, Stack, Text } from "@fluentui/react";
import { Ev2ExperimentStage, PipelineStatus } from "../../../../Types";
import {
  MPAC_TYPE,
  PROD_TYPE,
  RC_TYPE,
  READABLE_ACTION_MAP,
  ROLLBACK_TYPE,
  TREATMENT_STAGE_MAP,
} from "./ev2TreatmentStages.consts";
import {
  getEv2StageUrl,
  statusRenderer,
} from "../rolloutDetailsColumnRenderers";
import {
  getExpTreatment,
  normalizeEv2Status,
} from "../../../../Shared/shared.utils";
import { useMemo } from "react";
import { styles } from "./rolloutDetailsPanel.styles";
import { mergeClasses } from "@fluentui/react-components";
import { dateRenderer } from "../../../../Shared/TableRenderers";

interface RolloutDetailsPanelStagesListProps {
  /**
   * Array of EV2 experiment stages with status and details
   */
  ev2Stages: Ev2ExperimentStage[];
  /**
   * Base URL for EV2 portal links
   */
  ev2BaseLink: string;
  /**
   * Number of variants in the ABN experiment, used to determine if we should show A/B/n or A/B template in the panel
   */
  abnVariantCount?: number;
}

const RolloutDetailsPanelStagesList: React.FC<
  RolloutDetailsPanelStagesListProps
> = ({ ev2Stages, ev2BaseLink, abnVariantCount }) => {
  const stageType = useMemo(() => {
    if (!ev2Stages.length) {
      return "";
    }

    let stageType = "";
    const stageName = ev2Stages[0].stageName.toUpperCase();
    if (stageName.includes(ROLLBACK_TYPE.toUpperCase())) {
      return ROLLBACK_TYPE;
    }
    if (stageName.includes(MPAC_TYPE)) {
      stageType = MPAC_TYPE;
    } else if (stageName.includes(RC_TYPE)) {
      stageType = RC_TYPE;
    } else if (stageName.includes(PROD_TYPE)) {
      stageType = PROD_TYPE;
    }

    if (abnVariantCount && abnVariantCount > 2) {
      stageType = `${stageType}_ABN`;
    }

    return stageType;
  }, [abnVariantCount, ev2Stages]);

  const gridItems: StageRowItemType[] = useMemo(() => {
    const templateStages = TREATMENT_STAGE_MAP[stageType] || [];
    return templateStages.map((templateStage, _index) => {
      // Find matching EV2 stage data by comparing treatment and action
      const matchingEv2Stage = ev2Stages.find((ev2Stage) => {
        const ev2Treatment = getExpTreatment(ev2Stage.stageName);

        return (
          ev2Treatment === templateStage.treatment &&
          ev2Stage.rolloutAction === templateStage.action
        );
      });

      const stageName = `${templateStage.region} ${templateStage.treatment} - ${
        READABLE_ACTION_MAP[templateStage.action]
      }`;

      // If no matching data found, return pending state
      if (!matchingEv2Stage) {
        return {
          id: stageName,
          stage: stageName,
          status: PipelineStatus.PENDING,
          url: "",
          startTime: "",
        };
      }

      // Return merged data with display overrides
      return {
        ...matchingEv2Stage,
        id: stageName,
        stage: stageName,
        status: normalizeEv2Status(matchingEv2Stage),
        url: getEv2StageUrl(matchingEv2Stage, ev2BaseLink) ?? "",
      };
    });
  }, [ev2Stages, stageType, ev2BaseLink]);

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      {/* Header Row */}
      <Stack horizontal>
        <Stack.Item grow={1}>
          <Text className={styles.stageListHeaderText}>Stage</Text>
        </Stack.Item>
        <Stack.Item className={styles.startTimeCell}>
          <Text className={styles.stageListHeaderText}>Start Time</Text>
        </Stack.Item>
      </Stack>
      {/* Status Rows */}
      <Stack>
        {gridItems.map((item, index) => {
          return (
            <StageRowItem
              key={item.id}
              item={item}
              isLast={index === gridItems.length - 1}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

interface StageRowItemType extends Partial<Ev2ExperimentStage> {
  id: string;
  stage: string;
  status: PipelineStatus;
  url: string;
  startTime: string;
  [key: string]: any;
}

const StageRowItem: React.FC<{
  item: StageRowItemType;
  isLast: boolean;
}> = ({ item, isLast }) => {
  const error = item.error?.reason;
  return (
    <Stack key={item.id}>
      <Stack horizontal verticalAlign="center">
        {/* Stage Cell */}
        <Stack.Item grow={1}>
          {statusRenderer(
            item.stage,
            item.status,
            false,
            item.url,
            undefined,
            undefined,
            "RolloutDetailsPanelStagesList",
          )}
        </Stack.Item>
        {/* Start Time Cell */}
        <Stack.Item className={styles.startTimeCell}>
          {dateRenderer(item.startTime)}
        </Stack.Item>
      </Stack>
      <Stack
        horizontal
        verticalAlign="stretch"
        className={mergeClasses(!isLast && styles.stageSeperatorCell)}
      >
        <Stack grow={1} className={styles.errorMessageBarContainer}>
          {error && (
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              truncated={true}
              title={error}
            >
              {error}
            </MessageBar>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default RolloutDetailsPanelStagesList;
